import React, { Suspense, lazy, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { IconChevronLeft, IconLoader, IconX } from '@tabler/icons-react'
import { getFileIcon } from '../../utils/helpers'
import { getPreviewCategory } from './fileTypeRegistry'
import { ImagePreviewViewer } from './image/ImagePreviewViewer'
import { PreviewErrorState } from './PreviewErrorState'
import { PreviewErrorType } from './types'
import { PreviewUnsupportedFiles } from './PreviewUnsupportedFiles'
import { UseDocumentCitationPreviewResult } from './useDocumentCitationPreview'

// react-pdf pulls in pdfjs-dist, which is sizeable — lazy load it so opening the Citations panel
// never pays that bundle cost unless a PDF citation is actually previewed.
const PdfPreviewViewer = lazy(() =>
  import('./pdf/PdfPreviewViewer').then((mod) => ({ default: mod.PdfPreviewViewer }))
)

const SpreadsheetPreviewViewer = lazy(() =>
  import('./spreadsheet/SpreadsheetPreviewViewer').then((mod) => ({
    default: mod.SpreadsheetPreviewViewer
  }))
)

const WordDocumentPreviewViewer = lazy(() =>
  import('./word/WordDocumentPreviewViewer').then((mod) => ({
    default: mod.WordDocumentPreviewViewer
  }))
)

const PowerPointPreviewViewer = lazy(() =>
  import('./powerpoint/PowerPointPreviewViewer').then((mod) => ({
    default: mod.PowerPointPreviewViewer
  }))
)

const TextPreviewViewer = lazy(() =>
  import('./text/TextPreviewViewer').then((mod) => ({ default: mod.TextPreviewViewer }))
)

const PreviewLoader = () => (
  <div className="flex h-full items-center justify-center" role="status" aria-live="polite">
    <IconLoader size={28} className="theme-icon-muted animate-spin" aria-hidden="true" />
    <span className="sr-only">Loading document preview</span>
  </div>
)

const ERROR_COPY: Partial<
  Record<Exclude<PreviewErrorType, null>, { title: string; message: string }>
> = {
  'missing-document': {
    title: 'Document unavailable',
    message: 'We could not find a document to preview for this citation.'
  },
  'load-failure': {
    title: 'Preview failed to load',
    message: 'Something went wrong while loading this document. Please try again.'
  }
}

interface DocumentCitationPreviewPanelProps {
  preview: UseDocumentCitationPreviewResult
  onBack?: () => void
  onClose?: () => void
}

export const DocumentCitationPreviewPanel: React.FC<DocumentCitationPreviewPanelProps> = ({
  preview,
  onBack,
  onClose
}) => {
  const userId = useSelector((state: any) => state?.profileReducer?.profile?.email) || 'unknown'
  const {
    selectedCitation,
    previewOpen,
    activeHighlightIndex,
    previewLoadingState,
    highlightedLocations,
    previewError,
    previewCategory,
    blobUrl,
    closePreview,
    goToNextHighlight,
    goToPrevHighlight,
    retryLoad,
    downloadFallback,
    handleViewerError
  } = preview

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const handleBack = onBack || closePreview

  useEffect(() => {
    if (previewOpen) closeButtonRef.current?.focus()
  }, [previewOpen])

  useEffect(() => {
    if (!previewOpen) return undefined

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        handleBack()
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [previewOpen, handleBack])

  if (!previewOpen || !selectedCitation) return null

  const category = previewCategory || getPreviewCategory(selectedCitation.title)

  const renderBody = () => {
    if (previewLoadingState === 'loading') {
      return <PreviewLoader />
    }

    if (previewLoadingState === 'error') {
      if (previewError === 'unsupported-file-type') {
        return (
          <PreviewUnsupportedFiles title={selectedCitation.title} onDownload={downloadFallback} />
        )
      }

      const copy = previewError ? ERROR_COPY[previewError] : undefined
      if (copy) {
        return (
          <PreviewErrorState
            title={copy.title}
            message={copy.message}
            onRetry={previewError === 'load-failure' ? retryLoad : undefined}
          />
        )
      }
    }

    if (!blobUrl) return null

    if (category === 'pdf') {
      const citationWithCategory = { ...selectedCitation, category }
      return (
        <Suspense fallback={<PreviewLoader />}>
          <PdfPreviewViewer
            citation={citationWithCategory}
            pdfUrl={blobUrl}
            locations={highlightedLocations}
            activeHighlightIndex={activeHighlightIndex}
            fallbackPage={selectedCitation.pageNumber}
            userId={userId}
            onNextHighlight={goToNextHighlight}
            onPrevHighlight={goToPrevHighlight}
            onLoadError={handleViewerError}
          />
        </Suspense>
      )
    }

    if (category === 'image') {
      return (
        <ImagePreviewViewer
          imageUrl={blobUrl}
          title={selectedCitation.title}
          previewText={highlightedLocations[activeHighlightIndex]?.textSnippet}
        />
      )
    }

    if (category === 'spreadsheet') {
      const citationWithCategory = { ...selectedCitation, category }
      return (
        <Suspense fallback={<PreviewLoader />}>
          <SpreadsheetPreviewViewer
            spreadsheetUrl={blobUrl}
            title={selectedCitation.title}
            previewText={highlightedLocations[activeHighlightIndex]?.textSnippet}
            initialSheetNumber={selectedCitation.pageNumber}
            citation={citationWithCategory}
            userId={userId}
            onLoadError={handleViewerError}
          />
        </Suspense>
      )
    }

    if (category === 'document') {
      const citationWithCategory = { ...selectedCitation, category }
      return (
        <Suspense fallback={<PreviewLoader />}>
          <WordDocumentPreviewViewer
            documentUrl={blobUrl}
            previewText={highlightedLocations[activeHighlightIndex]?.textSnippet}
            initialPageNumber={selectedCitation.pageNumber}
            citation={citationWithCategory}
            userId={userId}
            onLoadError={handleViewerError}
          />
        </Suspense>
      )
    }

    if (category === 'presentation') {
      const citationWithCategory = { ...selectedCitation, category }
      return (
        <Suspense fallback={<PreviewLoader />}>
          <PowerPointPreviewViewer
            citation={citationWithCategory}
            presentationUrl={blobUrl}
            locations={highlightedLocations}
            activeHighlightIndex={activeHighlightIndex}
            fallbackPage={selectedCitation.pageNumber}
            userId={userId}
            onNextHighlight={goToNextHighlight}
            onPrevHighlight={goToPrevHighlight}
            onLoadError={handleViewerError}
          />
        </Suspense>
      )
    }

    if (category === 'text') {
      const citationWithCategory = { ...selectedCitation, category }
      return (
        <Suspense fallback={<PreviewLoader />}>
          <TextPreviewViewer
            textUrl={blobUrl}
            title={selectedCitation.title}
            previewText={highlightedLocations[activeHighlightIndex]?.textSnippet}
            citation={citationWithCategory}
            userId={userId}
            onLoadError={handleViewerError}
          />
        </Suspense>
      )
    }

    return null
  }

  const modalContent = (
    <div
      role="region"
      aria-labelledby="citation-preview-title"
      className="flex h-full w-full flex-col"
      data-testid="document-citation-preview-panel">
      <div className="rounded-tl-lg flex items-center gap-50 bg-primary p-1 text-white">
        <button
          ref={closeButtonRef}
          type="button"
          className="flex-shrink-0 appearance-none rounded-full border-0 bg-transparent p-50 text-white transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          aria-label="Back to citations"
          onClick={handleBack}>
          <IconChevronLeft size={18} />
        </button>
        <span className="flex flex-shrink-0 items-center justify-center" aria-hidden="true">
          {getFileIcon(selectedCitation.title)}
        </span>
        <div className="min-w-0 flex-1">
          <h2
            id="citation-preview-title"
            className="text-modal-title mb-0 truncate font-semibold text-white">
            {selectedCitation.title}
          </h2>
          {selectedCitation.databoxId && (
            <p className="mb-0 truncate text-body-small text-white/80">
              Databox: {selectedCitation.databoxId}
            </p>
          )}
        </div>
        {onClose && (
          <IconX
            data-testid="close-document-preview"
            className="flex-shrink-0 cursor-pointer"
            color="var(--chat-theme-primary-light)"
            size={20}
            aria-label="Close citations"
            onClick={onClose}
          />
        )}
      </div>

      {previewError === 'missing-location' && previewLoadingState === 'loaded' && (
        <div
          className="bg-light-warning theme-text-heading px-1 py-50 text-body-small"
          role="status">
          No highlight location is available for this citation.
        </div>
      )}

      <div className={`flex-1 overflow-hidden ${category === 'presentation' ? '' : 'p-1'}`}>
        {renderBody()}
      </div>
    </div>
  )

  return modalContent
}
